import { useEffect, useRef, type MutableRefObject, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { navLabel, type NavKey } from '@/i18n/keys/nav';
import { buildDocumentTitle, pageHeadNavigationState, titleText } from './pageHeadTitle';

export type PageHeadDensity = 'comfortable' | 'compact';

/**
 * Exactly one of `eyebrow` / `eyebrowKey` (#2109 L6, rulings brief B7). A
 * page whose nav row is keyed passes the SAME constant the nav data uses,
 * `eyebrowKey={NAV_KEY.tasks}`, and the eyebrow resolves in the active
 * locale; the eyebrow-parity guard reads that shape as a literal. A string
 * `eyebrow` stays for the pages R3/R4 have not converted yet.
 */
type EyebrowProp =
  | { eyebrow: string; eyebrowKey?: never }
  | { eyebrowKey: NavKey; eyebrow?: never };

type PageHeadProps = EyebrowProp & {
  /**
   * The display-rung headline. OPTIONAL since 2026-09-10 (DECISIONS "The
   * headline goes"): a parent hub page — Health, Finances, Tasks, Calendar —
   * renders no headline at all. With `title` omitted the eyebrow itself is the
   * `<h1>`: same label rung, same route-aware `document.title` ("Tasks · TRF"),
   * same focus target on navigation, and the eyebrow-parity guard still finds
   * one `PageHead` per depth-0 page. `lede` and `icon` are ignored without it.
   */
  title?: ReactNode;
  lede?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
  /**
   * A section row rendered BETWEEN the eyebrow and the H1 — for a page whose
   * headline is the section itself (Health, 2026-09-09): the tabs sit above
   * the title so the row reads as navigation, and the title changes with it.
   */
  tabs?: ReactNode;
  /**
   * The actions stack under the title below `sm` and may shrink and wrap beside it from
   * `sm` up, instead of holding one unshrinkable column (the Committee header, QA B1
   * 2026-10-26: two buttons pushed a 390 px page to 799 px). Opt-in, so no other header moves.
   */
  wrapActions?: boolean;
  /**
   * With `wrapActions`: the width from which the actions sit beside the title. Default `sm`.
   * The Committee header uses `xl`, so its title stays on one line at 1024 px with the app
   * sidebar open (workspace redesign round 3).
   */
  actionsBesideFrom?: 'sm' | 'xl';
  density?: PageHeadDensity;
  className?: string;
  /**
   * Text used in place of the H1's own text when building `document.title`.
   * The `· {section} · TRF` grammar still applies — this replaces only the
   * leading name. For a title whose ReactNode reads badly flattened, or one
   * that is a greeting rather than a page name ("Good afternoon, Mario").
   */
  documentTitle?: string;
};

// Base classes (no flex/gap). When an icon is rendered the h1 becomes a flex
// row; the title is then wrapped in a single <span> (see render) so multi-
// fragment titles like "...covered <em>so far</em>. We need…" stay one flex
// item and don't get a spurious gap inserted between the words / trailing period.
const titleClass: Record<PageHeadDensity, string> = {
  comfortable:
    'm-0 font-serif font-normal leading-[1.05] tracking-[-0.012em] text-stevens-ink text-[clamp(28px,3.6vw,40px)]',
  compact:
    'm-0 font-serif font-normal leading-[1.1] tracking-[-0.01em] text-stevens-ink text-[clamp(22px,2.6vw,28px)]',
};

const titleWithIconClass: Record<PageHeadDensity, string> = {
  comfortable: 'flex items-center gap-2.5',
  compact: 'flex items-center gap-2',
};

const iconClass: Record<PageHeadDensity, string> = {
  comfortable: 'h-7 w-7 flex-shrink-0 text-stevens-indigo',
  compact: 'h-5 w-5 flex-shrink-0 text-stevens-indigo',
};

const ledeClass: Record<PageHeadDensity, string> = {
  comfortable: 'text-stevens-ink-3 mt-1.5 text-[14.5px]',
  compact: 'text-stevens-ink-3 mt-1 text-[14px]',
};

const eyebrowClass =
  'block font-label text-label font-medium text-stevens-ink-3 mb-1';

/** The H1 is a focus target on navigation (§4.8), never a tab stop, and it
 *  draws no ring at all (#1950). A `tabIndex={-1}` heading cannot be reached
 *  by Tab, so the "keyboard visit" a `focus-visible` ring was kept for never
 *  happens; the only focus it ever gets is the programmatic move. Chrome
 *  matches that move as `:focus-visible` whenever the last input was a key
 *  (signing in with Enter) or there was none, so the ring painted around the
 *  headline on arrival — with Tailwind's default WHITE ring offset, a white
 *  frame in dark mode — until the next click. */
const h1FocusClass = 'outline-none';

function RouteAwareHead({
  h1Ref,
  documentTitle,
}: {
  h1Ref: MutableRefObject<HTMLHeadingElement | null>;
  documentTitle: string;
}) {
  const { pathname } = useLocation();

  // Declared FIRST so it commits first: §4.4 — "the title is set before focus
  // moves, and there is no aria-live announcer; the focused H1 is the
  // announcement." A screen reader that reads the document title on focus
  // must find the new one already in place.
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  useEffect(() => {
    if (pageHeadNavigationState.lastPathname !== null && pageHeadNavigationState.lastPathname !== pathname) {
      // Never steal focus out of an open dialog — a route change under a modal
      // (a background refresh, a redirect) would otherwise drop the reader
      // behind the scrim, outside the focus trap.
      const modal = document.querySelector('[role="dialog"],[role="alertdialog"]');
      if (!modal) h1Ref.current?.focus();
    }
    pageHeadNavigationState.lastPathname = pathname;
    // A `?tab=` change must NOT move focus (§4.8), so only `pathname` is a
    // dependency — `useLocation()` returns a new object per search change.
  }, [pathname, h1Ref]);

  return null;
}

export function PageHead({
  eyebrow: eyebrowText,
  eyebrowKey,
  title,
  lede,
  icon: Icon,
  actions,
  tabs,
  wrapActions = false,
  actionsBesideFrom = 'sm',
  density = 'comfortable',
  className,
  documentTitle,
}: PageHeadProps) {
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  // Subscribes this head to language changes; the key itself resolves through
  // `navLabel` (the singleton's `t`, which accepts a `nav:` key from any
  // namespace binding). A keyed eyebrow resolves in the active locale; the
  // string form is used as given. Everything below — the H1,
  // `document.title` — reads the resolved value, so `pageHeadTitle.ts` is
  // unchanged.
  useTranslation();
  const eyebrow: string = eyebrowKey ? navLabel(eyebrowKey) : (eyebrowText as string);
  // `useLocation()` throws outside a Router, and PageHead is rendered bare by
  // component tests and by Storybook-style previews. Gate the ROUTE-AWARE
  // BEHAVIOUR on a conditional child rather than calling the hook
  // conditionally — the app itself is always inside a Router, so the only
  // thing the gate turns off is title/focus in a router-less harness.
  const inRouter = useInRouterContext();

  const headless = title === undefined || title === null;

  const head = (
    <div>
      {inRouter ? (
        <RouteAwareHead
          h1Ref={h1Ref}
          documentTitle={buildDocumentTitle(
            documentTitle ?? (headless ? eyebrow : titleText(title)),
            eyebrow,
          )}
        />
      ) : null}
      {headless ? (
        // No headline: the eyebrow IS the page's H1 — the nav label, on the
        // label rung, carrying the focus and title behaviour the display H1
        // used to. The tabs (if any) follow it; nothing else does.
        <h1 ref={h1Ref} tabIndex={-1} className={cn(eyebrowClass, h1FocusClass, tabs && 'mb-3')}>
          {eyebrow}
        </h1>
      ) : (
        <span className={eyebrowClass}>{eyebrow}</span>
      )}
      {tabs ? <div className={headless ? undefined : 'mb-4'}>{tabs}</div> : null}
      {headless ? null : (
        <>
          <h1
            ref={h1Ref}
            tabIndex={-1}
            className={cn(titleClass[density], h1FocusClass, Icon && titleWithIconClass[density])}
          >
            {Icon ? <Icon className={iconClass[density]} /> : null}
            {/* With an icon the h1 is a flex row; wrap the title in ONE span so a
                multi-fragment title (text + <Emphasis> + text) stays a single flex
                item and the gap isn't inserted between its words (which detaches the
                trailing period). No-icon path renders the title inline as before. */}
            {Icon ? <span>{title}</span> : title}
          </h1>
          {lede ? <p className={ledeClass[density]}>{lede}</p> : null}
        </>
      )}
    </div>
  );

  if (!actions) return <div className={className}>{head}</div>;

  if (wrapActions) {
    return (
      <div
        className={cn(
          'flex min-w-0 flex-col gap-3',
          actionsBesideFrom === 'xl'
            ? 'xl:flex-row xl:items-start xl:justify-between xl:gap-4'
            : 'sm:flex-row sm:items-start sm:justify-between sm:gap-4',
          className,
        )}
        data-testid="page-head-wrap"
      >
        <div className="min-w-0">{head}</div>
        <div className={cn('min-w-0', actionsBesideFrom === 'xl' ? 'xl:max-w-[60%]' : 'sm:max-w-[60%]')}>{actions}</div>
      </div>
    );
  }

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      {head}
      <div className="flex-shrink-0">{actions}</div>
    </div>
  );
}

export function Emphasis({ children }: { children: ReactNode }) {
  return <em className="not-italic text-stevens-indigo">{children}</em>;
}
